import Box from '@mui/material/Box';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  memo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { type SxProps, type Theme } from '@mui/material/styles';

import { IconPlus as AddIcon } from '@/components/icon/tabler/IconPlus';
import { IconCheck as CheckIcon } from '@/components/icon/tabler/IconCheck';
import { IconX as CloseIcon } from '@/components/icon/tabler/IconX';
import { gray, green, red, controlTokens, surfaceTokens } from '@/theme/themePrimitives';
import SignalPulse from '@/components/Animation/SignalPulse';
import {
  chipAvatarGradient,
  chipDotColors,
  chipVariantSx,
  chipSelectedSx,
} from '@/components/Chip/utils';
import type { ChipColor, ChipDot, ChipTone, ChipVariant } from '@/components/Chip/types';

type ChipSize = 'xxsmall' | 'xsmall' | 'small' | 'medium';

const CHIP_SIZE_SPECS = {
  xxsmall: {
    h: '18px',
    fontSize: '10px',
    gap: '4px',
    iconSz: '11px',
    avatarSz: 12,
    deleteBtnSz: '11px',
    deleteIconSz: '7px',
    pl: { avatar: '4px', icon: '6px', default: '7px' },
    pr: { delete: '4px', default: '7px' },
  },
  xsmall: {
    h: '22px',
    fontSize: '10.5px',
    gap: '4px',
    iconSz: '12px',
    avatarSz: 16,
    deleteBtnSz: '14px',
    deleteIconSz: '9px',
    pl: { avatar: '4px', icon: '6px', default: '8px' },
    pr: { delete: '4px', default: '8px' },
  },
  small: {
    h: '26px',
    fontSize: '11.5px',
    gap: '5px',
    iconSz: '15px',
    avatarSz: 18,
    deleteBtnSz: '16px',
    deleteIconSz: '10px',
    pl: { avatar: '5px', icon: '8px', default: '10px' },
    pr: { delete: '5px', default: '10px' },
  },
  medium: {
    h: '30px',
    fontSize: '12.5px',
    gap: '6px',
    iconSz: '17px',
    avatarSz: 20,
    deleteBtnSz: '18px',
    deleteIconSz: '11px',
    pl: { avatar: '6px', icon: '10px', default: '12px' },
    pr: { delete: '6px', default: '12px' },
  },
} as const satisfies Record<
  ChipSize,
  {
    h: string;
    fontSize: string;
    gap: string;
    iconSz: string;
    avatarSz: number;
    deleteBtnSz: string;
    deleteIconSz: string;
    pl: { avatar: string; icon: string; default: string };
    pr: { delete: string; default: string };
  }
>;

export interface ChipProps {
  /** Visual treatment. Defaults to 'tonal'. */
  variant?: ChipVariant;
  /** Fill / accent color — used by 'solid' and 'outline'. */
  color?: ChipColor;
  /** Tint — used by 'tonal'. Defaults to 'gray'. */
  tone?: ChipTone;
  label: ReactNode;
  /** Leading icon. */
  icon?: ReactNode;
  // status variant
  dot?: ChipDot;
  pulse?: boolean;
  // entity variant
  avatar?: { src?: string; initials: string; bg?: string } | ReactNode;
  // interactive states
  selected?: boolean;
  onToggle?: () => void;
  onDelete?: (e: React.MouseEvent) => void;
  deleteLabel?: string;
  isAdd?: boolean;
  disabled?: boolean;
  // actions
  /** Also fired by Enter/Space on a non-button root, hence the keyboard event. */
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  href?: string;
  target?: string;
  rel?: string;
  size?: ChipSize;
  sx?: SxProps<Theme>;
  className?: string;
}

type ChipEntityAvatar = { src?: string; initials: string; bg?: string };

function isChipEntityAvatar(value: ChipProps['avatar']): value is ChipEntityAvatar {
  return !!value && typeof value === 'object' && !isValidElement(value) && 'initials' in value;
}

function withChipAvatarSize(avatar: ReactNode, avatarSz: number): ReactNode {
  if (!isValidElement(avatar)) return avatar;

  const existingSx = (avatar.props as { sx?: SxProps<Theme> }).sx;
  const baseSx =
    existingSx && typeof existingSx === 'object' && !Array.isArray(existingSx) ? existingSx : {};

  return cloneElement(avatar as ReactElement<{ sx?: SxProps<Theme> }>, {
    sx: {
      ...baseSx,
      width: avatarSz,
      height: avatarSz,
      border: 0,
    },
  });
}

type ChipAllProps = ChipProps & Omit<React.HTMLAttributes<HTMLElement>, keyof ChipProps>;

const ChipComponent = forwardRef<HTMLElement, ChipAllProps>(
  (
    {
      variant = 'tonal',
      color = 'info',
      tone = 'gray',
      label,
      icon,
      dot = 'neutral',
      pulse = false,
      avatar,
      selected = false,
      onToggle,
      onDelete,
      deleteLabel,
      isAdd = false,
      disabled = false,
      onClick,
      onMouseDown,
      href,
      target,
      rel,
      size = 'medium',
      sx: propSx,
      className,
      ...rest
    },
    ref,
  ) => {
    const isInteractive = !!(onClick || onToggle || href || isAdd);
    const isStatus = variant === 'status';
    const sizeSpec = CHIP_SIZE_SPECS[size];
    const { h, fontSize, gap, iconSz, avatarSz, deleteBtnSz, deleteIconSz } = sizeSpec;

    const leadingEl = isAdd
      ? (icon ?? <AddIcon />)
      : selected && onToggle
        ? (icon ?? <CheckIcon />)
        : icon;

    const hasLeadingAvatar = !!avatar;
    const hasLeadingIcon = !hasLeadingAvatar && (!!leadingEl || isStatus);
    const pl = hasLeadingAvatar
      ? sizeSpec.pl.avatar
      : hasLeadingIcon
        ? sizeSpec.pl.icon
        : sizeSpec.pl.default;
    const pr = onDelete ? sizeSpec.pr.delete : sizeSpec.pr.default;

    const rootComponent = href ? 'a' : isInteractive && !onDelete ? 'button' : 'span';

    const rootExtras: Record<string, unknown> = {};
    if (href) {
      rootExtras.href = disabled ? undefined : href;
      rootExtras.target = target;
      rootExtras.rel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);
      if (disabled) rootExtras.tabIndex = -1;
    } else if (rootComponent === 'button') {
      rootExtras.type = 'button';
      rootExtras.disabled = disabled;
    } else if (isInteractive) {
      rootExtras.role = 'button';
      rootExtras.tabIndex = disabled ? -1 : 0;
    }
    if (isInteractive && disabled) rootExtras['aria-disabled'] = true;
    if (onToggle) rootExtras['aria-pressed'] = selected;

    return (
      <Box
        ref={ref}
        component={rootComponent}
        {...rest}
        {...rootExtras}
        className={className}
        onClick={
          isInteractive && !disabled
            ? (e: React.MouseEvent) => {
                onToggle?.();
                onClick?.(e);
              }
            : undefined
        }
        onMouseDown={onMouseDown}
        onKeyDown={
          isInteractive && !disabled && rootComponent !== 'button'
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle?.();
                  onClick?.(e);
                }
              }
            : undefined
        }
        sx={[
          sx.root({ h, gap, pl, pr, fontSize, isInteractive, disabled }),
          chipVariantSx(variant, color, tone, isAdd, isInteractive),
          chipSelectedSx(selected, isAdd),
          ...(Array.isArray(propSx) ? propSx : propSx ? [propSx] : []),
        ]}
      >
        {isStatus && (
          <Box sx={sx.statusDotWrapper}>
            <SignalPulse size={8} color={chipDotColors[dot]} pulse={pulse} />
          </Box>
        )}

        {isChipEntityAvatar(avatar) ? (
          <Box
            sx={sx.entityAvatar({
              avatarSz,
              bg: avatar.bg ?? chipAvatarGradient,
            })}
          >
            {avatar.src ? (
              <Box component="img" src={avatar.src} sx={sx.avatarImg} />
            ) : (
              <Box
                component="span"
                sx={sx.avatarInitials(avatar.bg ? surfaceTokens.paper : controlTokens.primary)}
              >
                {avatar.initials}
              </Box>
            )}
          </Box>
        ) : avatar ? (
          <Box sx={sx.avatarSlot({ avatarSz })}>{withChipAvatarSize(avatar, avatarSz)}</Box>
        ) : null}

        {leadingEl && <Box sx={sx.leading({ iconSz })}>{leadingEl}</Box>}

        <Box component="span" sx={sx.label({ pulse: isStatus && pulse })}>
          {label}
        </Box>

        {onDelete && (
          <Box
            component="button"
            type="button"
            disabled={disabled}
            className="chip-delete"
            aria-label={deleteLabel}
            onKeyDown={(event) => event.stopPropagation()}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (!disabled) onDelete(e);
            }}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            sx={sx.deleteBtn({ disabled, btnSz: deleteBtnSz, iconSz: deleteIconSz })}
          >
            <Box component="div" sx={sx.deleteBtnBg} />
            <CloseIcon sx={{ position: 'relative', zIndex: 1 }} />
          </Box>
        )}
      </Box>
    );
  },
);

ChipComponent.displayName = 'Chip';

export default memo(ChipComponent);

const sx = {
  root: ({
    h,
    gap,
    pl,
    pr,
    fontSize,
    isInteractive,
    disabled,
  }: {
    h: string;
    gap: string;
    pl: string;
    pr: string;
    fontSize: string;
    isInteractive: boolean;
    disabled: boolean;
  }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap,
    height: h,
    borderRadius: '9999px',
    border: '1px solid',
    fontSize,
    fontWeight: 600,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    textDecoration: 'none',
    transition: 'all 0.14s',
    cursor: isInteractive ? 'pointer' : 'default',
    background: 'none',
    paddingLeft: pl,
    paddingRight: pr,
    outline: 'none',
    userSelect: 'none',
    opacity: disabled ? 0.45 : 1,
    pointerEvents: disabled ? 'none' : undefined,
    '&:focus-visible': {
      boxShadow: controlTokens.focusRing,
    },
    ...(isInteractive && {
      '&:active:not(:disabled)': { transform: 'translateY(0)' },
    }),
  }),
  statusDotWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  entityAvatar: ({ bg, avatarSz }: { bg: string; avatarSz: number }) => ({
    width: avatarSz,
    height: avatarSz,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
    background: bg,
    overflow: 'hidden',
  }),
  avatarSlot: ({ avatarSz }: { avatarSz: number }) => ({
    width: avatarSz,
    height: avatarSz,
    flexShrink: 0,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' as const },
  avatarInitials: (color: string) => ({ fontSize: '10px', fontWeight: 700, color }),
  onlineIndicator: {
    position: 'absolute' as const,
    bottom: 0,
    right: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: green[400],
    border: `1.5px solid ${surfaceTokens.paper}`,
  },
  leading: ({ iconSz }: { iconSz: string }) => ({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    fontSize: iconSz,
    '& svg': { fontSize: 'inherit', display: 'block' },
  }),
  avatarSub: { fontSize: 'inherit', fontWeight: 500, opacity: 0.55, mr: '-2px' },
  label: ({ pulse }: { pulse: boolean }) => ({
    lineHeight: 1,
    opacity: pulse ? 0.8 : 1,
    paddingLeft: pulse ? '4px' : 0,
  }),
  deleteBtn: ({
    disabled,
    btnSz,
    iconSz,
  }: {
    disabled: boolean;
    btnSz: string;
    iconSz: string;
  }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: btnSz,
    height: btnSz,
    borderRadius: '50%',
    color: gray[600],
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
    flexShrink: 0,
    transition: 'all 0.12s',
    backgroundColor: 'transparent',
    '& svg': { fontSize: iconSz, display: 'block' },
    '&:hover': { color: red[400], '& div': { backgroundColor: red[100], opacity: 1 } },
    position: 'relative' as const,
  }),
  deleteBtnBg: {
    borderRadius: '50%',
    backgroundColor: gray[100],
    opacity: 0.45,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
};
